import { useState, useEffect } from "react";
import { ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { useShop } from "../context/ShopContext";
import { formatPrice } from "../data/products";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const ProductDetail = ({ product, open, onOpenChange }) => {
  const { lang, t, addToCart, productMedia, productVideo, seriesLabel } = useShop();
  const sizes = product?.available_dims?.length ? product.available_dims : [product?.dims].filter(Boolean);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    if (open && product) setSelected(sizes[0] || null);
  }, [open, product]);

  if (!product) return null;

  const video = productVideo(product);
  const photo = productMedia(product);

  const handleAdd = () => {
    addToCart(product.id, selected);
    toast.success(`${product.name[lang]} (${selected}) — ${t.products.added}`);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-cream border-brown/10 text-brown sm:max-w-3xl max-h-[90vh] overflow-y-auto p-0"
        data-testid="product-detail-dialog"
      >
        <div className="grid grid-cols-1 md:grid-cols-2">
          <div className="relative aspect-[4/3] md:aspect-auto md:min-h-[420px] bg-cream-dark">
            {video ? (
              <video
                src={video}
                poster={photo || undefined}
                controls
                preload="metadata"
                className="w-full h-full object-cover"
                data-testid="detail-video"
              />
            ) : (
              <img
                src={photo}
                alt={product.name[lang]}
                className="w-full h-full object-cover"
                data-testid="detail-image"
              />
            )}
            <span className="absolute top-5 left-5 px-3 py-1 bg-cream/90 backdrop-blur text-[10px] tracking-[0.2em] uppercase font-semibold text-brown">
              {seriesLabel(product.series)}
            </span>
          </div>

          <div className="p-8 md:p-10 flex flex-col">
            <DialogHeader>
              <p className="text-[11px] tracking-[0.25em] uppercase text-brown-muted mb-2">
                {sizes.join(" · ")}
              </p>
              <DialogTitle className="font-serif text-3xl font-medium text-brown text-left">
                {product.name[lang]}
              </DialogTitle>
            </DialogHeader>

            {product.description?.[lang] && (
              <p className="mt-4 text-sm md:text-base font-light leading-relaxed text-brown-light">
                {product.description[lang]}
              </p>
            )}

            <p className="mt-6 font-serif text-3xl font-medium text-brown" data-testid="detail-price">
              {formatPrice(product.price)}
              <span className="ml-2 text-sm font-sans font-light text-brown-muted">{t.products.from}</span>
            </p>

            <div className="mt-8">
              <p className="text-[11px] tracking-[0.25em] uppercase text-brown-muted mb-3">
                {t.variations.chooseSize}
              </p>
              <div className="flex flex-wrap gap-2" data-testid="detail-size-selector">
                {sizes.map((dims) => (
                  <button
                    key={dims}
                    onClick={() => setSelected(dims)}
                    className={`px-5 py-2.5 rounded-full text-xs font-medium tracking-wide transition-colors duration-300 border ${
                      selected === dims
                        ? "bg-brown text-cream border-brown"
                        : "border-brown/20 text-brown-light hover:border-olive hover:text-olive-dark"
                    }`}
                    data-testid={`detail-size-${dims.replace(/[^0-9]/g, "-")}`}
                  >
                    {dims}
                  </button>
                ))}
              </div>
              <p className="mt-3 text-xs font-light text-brown-muted">{t.variations.inCartNote}</p>
            </div>

            <p className="mt-4 inline-flex items-center gap-2 text-xs tracking-wide text-olive-dark">
              {t.catalog.customNote}
            </p>

            <button
              onClick={handleAdd}
              disabled={!selected}
              className="mt-auto pt-8 inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full bg-olive text-cream text-sm font-medium tracking-wide hover:bg-olive-dark disabled:opacity-60 transition-colors duration-300"
              data-testid="detail-add-to-cart"
            >
              <ShoppingBag size={16} />
              {t.products.addToCart} — {formatPrice(product.price)}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProductDetail;
